import { useCallback } from 'react';

import { HubConnectionState } from '@microsoft/signalr';
import {
  BridgeRequestMethod,
  BridgeSimpleSubscriptionMethod,
  BridgeSubscriptionMethod,
  EventSubscriptionConfig,
  IdAndEventSubscriptionConfig,
  IdSubscriptionConfig,
  ProviderSubscriptionConfig,
  type BridgeActionMethod,
  type IGetInventoryConfig,
  type IGetPensConfig,
} from '@scaleaq/sitecontrol-lib';

import { useCurrentSiteConnection } from '../connection';

type SubscriptionMethod =
  | BridgeActionMethod
  | BridgeRequestMethod
  | BridgeSimpleSubscriptionMethod
  | BridgeSubscriptionMethod;

const sleep = (t: number) =>
  new Promise(f => {
    setTimeout(f, t);
  });

interface IActionConfig {
  action: string;
  value: string;
}

type UseBridgeEventInvoke =
  | EventSubscriptionConfig
  | IActionConfig
  | IGetPensConfig
  | IGetInventoryConfig
  | IdAndEventSubscriptionConfig
  | IdSubscriptionConfig
  | ProviderSubscriptionConfig;

/**
 * Used by useBridgeRequest, useBridgeAction, useBridgeSubscription
 * This hook creates a signalR connection (useSitecontrolBridgeConnection) and subscribes to a named event
 * @param subscriptionMethod
 */
export const useBridgeEventSubscription = <Args extends UseBridgeEventInvoke>(
  subscriptionMethod: SubscriptionMethod
) => {
  const methodName = subscriptionMethod.toString();
  // get a connection to the signalR hub

  const { connection, isLoading, isError } = useCurrentSiteConnection();
  const subscribeToEvent = useCallback(
    async function invokeWhenConnected(args: Args) {
      try {
        if (connection?.state === HubConnectionState.Connected) {
          return await connection?.invoke(methodName, args);
        }
        if (connection?.state === HubConnectionState.Connecting) {
          await sleep(1000);
          return await invokeWhenConnected(args);
        }
      } catch (e) {
        console.error(
          `❌ useBridgeEventInvoke: Could not subscribe to "${methodName}"`,
          e,
          'args',
          args,
          e
        );
      }
      return;
    },
    [connection, methodName]
  );

  if (isLoading) {
    return { isLoading, isError };
  }
  return { subscribeToEvent, isLoading, isError };
};
